import React from 'react';
import styled from 'styled-components';

interface SiteSettings {
  whatsapp_number: string;
  whatsapp_message: string;
  whatsapp_property_message: string;
  whatsapp_lead_message: string;
  whatsapp_enabled: boolean;
  [key: string]: any;
}

interface WhatsAppSettingsProps {
  settings: SiteSettings;
  onUpdate: (updates: Partial<SiteSettings>) => void;
}

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Switch = styled.div<{ $enabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  
  input {
    display: none;
  }
  
  .switch {
    width: 50px;
    height: 24px;
    background: ${({ $enabled, theme }) => $enabled ? theme.colors.primary : '#ccc'};
    border-radius: 12px;
    position: relative;
    transition: background 0.3s;
    
    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: ${({ $enabled }) => $enabled ? '26px' : '2px'};
      width: 20px;
      height: 20px;
      background: white;
      border-radius: 50%;
      transition: left 0.3s;
    }
  }
`;

const HelpText = styled.small`
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

const VariableList = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const VariableItem = styled.div`
  margin-bottom: 0.25rem;
  
  code {
    background: #e9ecef;
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    font-family: monospace;
  }
`;

const WhatsAppSettings: React.FC<WhatsAppSettingsProps> = ({ settings, onUpdate }) => {
  const handleChange = (field: string, value: any) => {
    onUpdate({ [field]: value });
  };

  const handleToggleEnabled = () => {
    handleChange('whatsapp_enabled', !settings.whatsapp_enabled);
  };

  return (
    <>
      <Section>
        <SectionTitle>💬 Configurações do WhatsApp</SectionTitle>
        
        <FormGroup>
          <Label>Ativar WhatsApp</Label>
          <Switch $enabled={settings.whatsapp_enabled} onClick={handleToggleEnabled}>
            <input 
              type="checkbox" 
              checked={settings.whatsapp_enabled} 
              onChange={handleToggleEnabled}
              readOnly
            />
            <div className="switch" />
            <span>{settings.whatsapp_enabled ? 'Ativado' : 'Desativado'}</span>
          </Switch>
          <HelpText>Habilita o botão flutuante do WhatsApp e links de contato</HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="whatsapp_number">Número do WhatsApp</Label>
          <Input
            id="whatsapp_number"
            type="tel"
            value={settings.whatsapp_number}
            onChange={(e) => handleChange('whatsapp_number', e.target.value)}
            placeholder="5511999999999"
          />
          <HelpText>
            Número no formato internacional (código do país + DDD + número)
            <br />Exemplo: 5511999999999 (Brasil + 11 + 999999999)
          </HelpText>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>📝 Mensagens Personalizadas</SectionTitle>
        
        <FormGroup>
          <Label htmlFor="whatsapp_message">Mensagem Geral</Label>
          <TextArea
            id="whatsapp_message"
            value={settings.whatsapp_message}
            onChange={(e) => handleChange('whatsapp_message', e.target.value)}
            placeholder="Olá! Vim através do site e gostaria de mais informações sobre imóveis."
          />
          <HelpText>Mensagem padrão quando o usuário clica no botão do WhatsApp</HelpText>
          <VariableList>
            <strong>Variáveis disponíveis:</strong>
            <VariableItem><code>{'{site_url}'}</code> - URL do site</VariableItem>
          </VariableList>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="whatsapp_property_message">Mensagem para Imóveis</Label>
          <TextArea
            id="whatsapp_property_message"
            value={settings.whatsapp_property_message}
            onChange={(e) => handleChange('whatsapp_property_message', e.target.value)}
            placeholder="Olá! Tenho interesse no imóvel *{property_title}*. Gostaria de mais informações e agendar uma visita."
          />
          <HelpText>Mensagem enviada quando o usuário clica em "Falar no WhatsApp" em um imóvel específico</HelpText>
          <VariableList>
            <strong>Variáveis disponíveis:</strong>
            <VariableItem><code>{'{property_title}'}</code> - Título do imóvel</VariableItem>
            <VariableItem><code>{'{property_url}'}</code> - Link do imóvel</VariableItem>
            <VariableItem><code>{'{property_price}'}</code> - Preço do imóvel</VariableItem>
          </VariableList>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="whatsapp_lead_message">Mensagem para Leads</Label>
          <TextArea
            id="whatsapp_lead_message"
            value={settings.whatsapp_lead_message}
            onChange={(e) => handleChange('whatsapp_lead_message', e.target.value)}
            placeholder="Olá! Deixei meus dados no formulário do site. Meu nome é {name} e gostaria de conversar sobre imóveis."
          />
          <HelpText>Mensagem enviada quando um lead preenche um formulário e quer falar no WhatsApp</HelpText>
          <VariableList>
            <strong>Variáveis disponíveis:</strong>
            <VariableItem><code>{'{name}'}</code> - Nome do lead</VariableItem>
            <VariableItem><code>{'{phone}'}</code> - Telefone do lead</VariableItem>
            <VariableItem><code>{'{email}'}</code> - E-mail do lead</VariableItem>
            <VariableItem><code>{'{site_url}'}</code> - URL do site</VariableItem>
          </VariableList>
        </FormGroup>
      </Section>
    </>
  );
};

export default WhatsAppSettings;